import { runWithErrorReporting } from "$root/api/error-reporting";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import {
	MIN_NUMBER_OF_INSTRUMENTS,
	TARGET_PORTFOLIO_INSTRUMENT_LIMIT,
	UNIVERSE_INSTRUMENT_LIMIT,
} from "$root/functional-areas/const";
import {
	cashTickerToInstrument,
	covertInstrumentEditorToReviewTicker,
	useInstrumentEditorBuilder,
} from "$root/functional-areas/instruments-editor/builder";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import InstrumentCompositionEditor from "$root/functional-areas/instruments-editor/instrumentEditor";
import { spawnCopyCompositionDialog } from "$root/functional-areas/instruments-editor/spawn/copy-composition";
import { createInstrumentList, instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { customerDecimalPlaces, useUserValue } from "$root/functional-areas/user";
import { weightTotalStatus } from "$root/functional-areas/instruments-editor/weight-total";
import { generateIntentId, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { columnsDefinition } from "$root/ui-lib/table/column";
import type { CompositionTemplateEntityType } from "@eu.mdotm.api/sphere";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { Map, OrderedMap } from "immutable";
import { useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { PortfolioStudioTab } from "../PortfoliosStudio/portfolio-studio-tabs";
import { instrumentsEditorRepository } from "$root/functional-areas/instruments-editor/repository";
import { ManualCreationEntitiesEnum } from "./common";
import { spawnSubmitTargetPortfolioCreationDialog } from "./spawn-create-reference";
import { spawnSubmitUniverseCreationDialog } from "./spawn-create-universe";

function ManualCreation({
	entity: _entity,
	workspaceUid,
	copyComposition,
	preselectedTemplate,
	intentId,
}: {
	entity?: string;
	workspaceUid?: string;
	copyComposition?: string;
	preselectedTemplate?: string;
	intentId?: string;
}): React.JSX.Element {
	const entity = _entity === "REFERENCE" ? "TARGET_INVESTMENT" : "UNIVERSE";
	const [pathToNotBlock, setPathToNotBlock] = useState<string[]>([typedUrlForRoute("Login", {})]);

	const { t } = useTranslation();
	const { push, replace } = useTypedNavigation();
	const user = useUserValue();

	const instrumentBuilder = useInstrumentEditorBuilder({ composition: Map() });
	const compositionMapWithoutDeleteInstruments = useMemo(
		() => instrumentBuilder.getComposition({ removeDeleted: true }),
		[instrumentBuilder],
	);
	const totalCompositionWeight = instrumentBuilder.getTotal("weight");
	const weightTotalPrecision = customerDecimalPlaces(user);

	const instrumentsInComposition = useMemo(
		() =>
			//TBD: idk since in the universe u can't have an aggregate view of the portfolio but they are
			// directly saved as nest portfolio maybe we can consider to count as single instrument just for the universe
			compositionMapWithoutDeleteInstruments.reduce((sum, instrument) => {
				if (instrument.proxyOverwriteType === "PORTFOLIO_MIXED") {
					const instrumentsInInvestment = instrument.nofInstruments ?? 0;
					const instrumentsExcluded = instrument.nOfInstrumentExcluded ?? 0;
					return instrumentsInInvestment - instrumentsExcluded + sum;
				}

				return sum + 1;
			}, 0),
		[compositionMapWithoutDeleteInstruments],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <not needed instrumentBuilder.reset>
	useEffect(() => {
		if (copyComposition) {
			spawnCopyCompositionDialog({
				entity: "REFERENCE_INVESTMENT",
				initialSidePanelFilters: {
					search: "",
					filters: [
						{
							key: "TYPE",
							relation: "EQUAL",
							type: "FINITE_SET_TYPE",
							value: ["STANDARD_BENCHMARK"] satisfies CompositionTemplateEntityType[],
						},
					],
				},
				preselectedTemplate,
				onSubmitAsync: async (data) => {
					await runWithErrorReporting(
						async () => {
							const { instruments, cashTicker } = await instrumentsRepository.copyComposition.fetchQuery(
								data.uuid,
								data.type,
							);

							instrumentBuilder.reset(({ cash }) => ({
								composition: OrderedMap<string, Instrument>(instruments.map((x) => [x.id, x])),
								cash: cashTicker ? cashTickerToInstrument(cashTicker) : cash,
							}));

							replace("Portfolios/ManualCreation", {
								entity: ManualCreationEntitiesEnum.Reference,
								intentId: generateIntentId(),
							});
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: "attempt to load entity template",
								payload: JSON.stringify(data),
							},
						},
					);
				},
			});
		}
	}, [replace, copyComposition, preselectedTemplate, workspaceUid]);

	const maxInstrumentsInComposition = hasAccess(user, { requiredService: "NUMBER_OF_INSTRUMENTS_CHECK_BYPASS" })
		? +Infinity
		: match(entity)
				.returnType<number>()
				.with("TARGET_INVESTMENT", () => TARGET_PORTFOLIO_INSTRUMENT_LIMIT)
				.with("UNIVERSE", () => UNIVERSE_INSTRUMENT_LIMIT)
				.exhaustive();

	const minInstrumentsInComposition = MIN_NUMBER_OF_INSTRUMENTS;

	const isSubmitDisabled = useMemo(() => {
		if (entity === "UNIVERSE") {
			return (
				instrumentsInComposition < minInstrumentsInComposition || instrumentsInComposition > maxInstrumentsInComposition
			);
		}

		return (
			weightTotalStatus(totalCompositionWeight, weightTotalPrecision) !== "exact" ||
			instrumentsInComposition < minInstrumentsInComposition ||
			instrumentsInComposition > maxInstrumentsInComposition
		);
	}, [
		entity,
		instrumentsInComposition,
		maxInstrumentsInComposition,
		minInstrumentsInComposition,
		totalCompositionWeight,
		weightTotalPrecision,
	]);

	const queryEditComposition = instrumentsRepository.newComposition.useQuery(entity);

	useQueryReactor(queryEditComposition, {
		onSuccess(editCompositionResponse) {
			const composition = editCompositionResponse.composition ?? [];
			const instruments = createInstrumentList({ composition, userInstrumentsMap: Map() });

			instrumentBuilder.reset(
				{
					composition: OrderedMap<string, Instrument>(instruments.map((x) => [x.id, x])),
					cash: editCompositionResponse.cashTicker
						? cashTickerToInstrument(editCompositionResponse.cashTicker)
						: undefined,
				},
				{ disableToggleDirty: true },
			);
		},
	});

	const crumbs = useCrumbs();
	const classifications = useAtomValue(userClassifications$);
	const columns = useMemo(
		() =>
			entity === "TARGET_INVESTMENT"
				? columnsDefinition.instrumentComposition(classifications ?? [])
				: columnsDefinition.instrumentUniverseComposition(classifications ?? []),
		[entity, classifications],
	);
	return (
		<>
			<PageHeader
				crumbs={match(entity)
					.with("TARGET_INVESTMENT", () => [...crumbs.portfolioStudio("References"), ...crumbs.newTargetPortfolio])
					.with("UNIVERSE", () => [...crumbs.portfolioStudio("Universes"), ...crumbs.newUniverse])
					.exhaustive()}
				titleAction={
					<div className="flex items-center space-x-2">
						<Button
							size="small"
							onClick={() =>
								push("PortfoliosStudio", {
									tab: match(entity)
										.returnType<PortfolioStudioTab>()
										.with("TARGET_INVESTMENT", () => PortfolioStudioTab.References)
										.with("UNIVERSE", () => PortfolioStudioTab.Universes)
										.exhaustive(),
								})
							}
							palette="tertiary"
							data-qualifier="CompositionEditor/Cancel"
						>
							{t("BUTTON.CANCEL")}
						</Button>

						<Button
							size="small"
							disabled={isSubmitDisabled}
							onClick={match(entity)
								.returnType<() => void>()
								.with(
									"TARGET_INVESTMENT",
									() => () =>
										spawnSubmitTargetPortfolioCreationDialog({
											onAsyncSubmit: async (form) => {
												const composition = covertInstrumentEditorToReviewTicker(
													Array.from(instrumentBuilder.getComposition({ removeDeleted: true }).values()),
												);
												const { identifier } = await instrumentsEditorRepository.create.targetInvestment({
													form,
													composition,
												});
												flushSync(() =>
													setPathToNotBlock([typedUrlForRoute("ReferenceDetails", { uuid: identifier! })]),
												);
												trackMixPanelEvent("reference.entityActions.create", {
													reference: { uuid: identifier!, name: form.name },
													intentId,
												});
												push("ReferenceDetails", { uuid: identifier! });
											},
										}),
								)
								.with(
									"UNIVERSE",
									() => () =>
										spawnSubmitUniverseCreationDialog({
											async onAsyncSubmit({ name }) {
												const hasPortfolioInComposition = instrumentBuilder
													.getComposition()
													.some((instrument) => instrument.proxyOverwriteType === "PORTFOLIO_MIXED");
												const composition = covertInstrumentEditorToReviewTicker(
													Array.from(instrumentBuilder.getComposition({ removeDeleted: true }).values()),
												);

												const { identifier } = await instrumentsEditorRepository.create.universe({
													form: {
														name,
														portfolioSavingMode: hasPortfolioInComposition ? "NEST_PORTFOLIOS" : "MIX_INSTRUMENTS",
													},
													composition,
												});

												flushSync(() =>
													setPathToNotBlock([typedUrlForRoute("UniverseDetails", { universeUuid: identifier! })]),
												);
												trackMixPanelEvent("universe.entityActions.create", {
													universe: { uuid: identifier!, name, type: "WITHOUT_GROUPS" },
													intentId,
												});
												push("UniverseDetails", { universeUuid: identifier! });
											},
										}),
								)
								.exhaustive()}
							palette="primary"
							data-qualifier="CompositionEditor/Save"
						>
							{t("BUTTON.DONE")}
						</Button>
					</div>
				}
			/>
			<ReactQueryWrapperBase query={queryEditComposition}>
				{() => (
					<div className="min-h-0 grow flex">
						<InstrumentCompositionEditor
							entity={entity}
							mode="new"
							instrumentBuilder={instrumentBuilder}
							minThreshold={minInstrumentsInComposition}
							limit={maxInstrumentsInComposition}
							columns={columns}
						/>
					</div>
				)}
			</ReactQueryWrapperBase>
			<LeavePrompt
				title={t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.TITLE")}
				when={instrumentBuilder.isDirty}
				pathToNotBlock={pathToNotBlock}
			>
				{t("NOTIFICATION_SETTINGS.LEAVE_PROMPT.MESSAGE")}
			</LeavePrompt>
		</>
	);
}

export default ManualCreation;
